import { useState, memo } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import { ProgressIndicator } from '@atlaskit/progress-indicator';

import { useIntl } from 'src/hooks/intl';
import { usePref } from 'src/hooks/use-pref';

import messages from './onboarding-dialog.i18n';
import * as styles from './onboarding-dialog.styled';
import bannerImageStep1 from './onboarding-step-1.png';
import bannerImageStep2 from './onboarding-step-2.png';
import bannerImageStep3 from './onboarding-step-3.png';

export const ONBOARDING_VIEWED_KEY = 'jira-tab-onboarding-viewed';
export const ONBOARDING_VIEWED_VALUE = 'viewed';

type Step = {
  bannerImage: string;
  title: FormattedMessage.MessageDescriptor;
  description: FormattedMessage.MessageDescriptor;
};

const steps: Step[] = [
  {
    bannerImage: bannerImageStep1,
    title: messages.step1.title,
    description: messages.step1.description,
  },
  {
    bannerImage: bannerImageStep2,
    title: messages.step2.title,
    description: messages.step2.description,
  },
  {
    bannerImage: bannerImageStep3,
    title: messages.step3.title,
    description: messages.step3.description,
  },
];

type BodyProps = {
  step: Step;
};

const Body = ({ step }: BodyProps) => {
  const intl = useIntl();
  return (
    <styles.OnboardingDialogBodyContainer>
      <styles.OnboardingDialogBannerImage
        src={step.bannerImage}
        alt={intl.formatMessage(step.title)}
      />
    </styles.OnboardingDialogBodyContainer>
  );
};

type FooterProps = {
  currentStepIndex: number;
  goToStep: (stepIndex: number) => void;
  onDismiss: () => void;
  step: Step;
};

const Footer = ({
  currentStepIndex,
  goToStep,
  onDismiss,
  step,
}: FooterProps) => {
  const intl = useIntl();

  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <styles.OnboardingDialogFooterContainer>
      <styles.OnboardingDialogFooterTitle>
        {intl.formatMessage(step.title)}
      </styles.OnboardingDialogFooterTitle>
      <styles.OnboardingDialogFooterDescription>
        {intl.formatMessage(step.description)}
      </styles.OnboardingDialogFooterDescription>
      <styles.OnboardingDialogFooterActionsContainer>
        <styles.OnboardingDialogFooterActionButtonLeft>
          {!isLastStep && (
            <Button appearance="subtle" onClick={onDismiss}>
              {intl.formatMessage(messages.common.dismiss)}
            </Button>
          )}
        </styles.OnboardingDialogFooterActionButtonLeft>
        <ProgressIndicator
          selectedIndex={currentStepIndex}
          values={steps}
          onSelect={({ index }: { index: number }) => goToStep(index)}
        />
        <styles.OnboardingDialogFooterActionButtonRight>
          {isLastStep ? (
            <Button appearance="primary" onClick={onDismiss}>
              {intl.formatMessage(messages.common.done)}
            </Button>
          ) : (
            <Button
              appearance="primary"
              onClick={() => goToStep(currentStepIndex + 1)}
              autoFocus
            >
              {intl.formatMessage(messages.common.next)}
            </Button>
          )}
        </styles.OnboardingDialogFooterActionButtonRight>
      </styles.OnboardingDialogFooterActionsContainer>
    </styles.OnboardingDialogFooterContainer>
  );
};

export const OnboardingDialog = memo(() => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [viewed, updatePref, isLoading] = usePref(ONBOARDING_VIEWED_KEY);

  if (viewed === ONBOARDING_VIEWED_VALUE || isLoading) {
    return null;
  }

  if (currentStepIndex < 0 || currentStepIndex >= steps.length) {
    return null;
  }

  const goToStep = (stepIndex: number) => {
    setCurrentStepIndex(stepIndex);
  };

  const handleDismiss = () => {
    setCurrentStepIndex(-1);
    updatePref(ONBOARDING_VIEWED_VALUE);
  };

  return (
    <ModalTransition>
      <styles.OnboardingDialogContainer>
        <ModalDialog
          width="large"
          onClose={handleDismiss}
          shouldCloseOnOverlayClick={false}
        >
          <Body step={steps[currentStepIndex]} />
          <Footer
            currentStepIndex={currentStepIndex}
            goToStep={goToStep}
            onDismiss={handleDismiss}
            step={steps[currentStepIndex]}
          />
        </ModalDialog>
      </styles.OnboardingDialogContainer>
    </ModalTransition>
  );
});
